import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  ArrowRight,
  Shield,
  BarChart3,
  TrendingUp,
  DollarSign,
  Wifi,
  Activity,
  PieChart,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  Cpu,
  Info
} from 'lucide-react';
import { DataIntegrityBadge, LastUpdated, PlatformFooter } from '@/components/common/PageElements';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Module Section Component
const ModuleSection = ({ icon: Icon, title, subtitle, children }) => (
  <section className="mb-10">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    <div className="pl-[52px]">
      {children}
    </div>
    <div className="border-b border-border/50 mt-10" />
  </section>
);

// Method Item Component
const MethodItem = ({ title, description, formula, formulaNote }) => (
  <div className="mb-4 last:mb-0">
    <h4 className="text-sm font-semibold text-foreground mb-1">{title}</h4>
    <p className="text-sm text-muted-foreground mb-2">{description}</p>
    {formula && (
      <code className="text-xs px-3 py-1.5 rounded bg-muted/50 text-muted-foreground font-mono inline-block">
        {formula}
      </code>
    )}
    {formulaNote && (
      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 italic">{formulaNote}</p>
    )}
  </div>
);

// Weight Distribution Chart Data
const weightData = [
  { name: 'Leadership Reputation', value: 25, color: 'hsl(var(--primary))' },
  { name: 'Historical Performance', value: 20, color: 'hsl(221, 83%, 53%)' },
  { name: 'Media Authority', value: 18, color: 'hsl(142, 76%, 36%)' },
  { name: 'Digital Presence', value: 15, color: 'hsl(38, 92%, 50%)' },
  { name: 'Global Recognition', value: 12, color: 'hsl(280, 65%, 60%)' },
  { name: 'Institutional Alignment', value: 10, color: 'hsl(0, 0%, 60%)' },
];

// Custom Tooltip for Chart
const ChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-2 shadow-lg">
        <p className="text-sm font-medium">{payload[0].name}</p>
        <p className="text-sm text-primary font-bold">{payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

export default function MethodologyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="methodology-page">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-12">
        {/* Page Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div></div>
            <div className="flex items-center gap-3">
              <DataIntegrityBadge status="verified" />
              <LastUpdated date="Feb 2026" />
            </div>
          </div>
          
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-2">Methodology & Standards</h1>
              <p className="text-base text-muted-foreground max-w-3xl leading-relaxed">
                Our brand intelligence platform combines industry-recognized valuation frameworks with 
                real-time public data signals to provide defensible, strategic insights for sports assets.
              </p>
            </div>
          </div>
          
          {/* Framework badges */}
          <div className="flex flex-wrap gap-3 p-4 rounded-xl bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">ISO 10668 Aligned</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border">
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">BSI-Aligned Methodology</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Interbrand-Aligned Valuation</span>
            </div>
          </div>
        </div>

        {/* Data Classification Legend */}
        <Card className="mb-10 bg-card border-border">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">Data Classification Legend</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-stable/5 border border-stable/20">
                <CheckCircle className="w-5 h-5 text-stable" />
                <div>
                  <p className="text-sm font-medium text-foreground">Verified</p>
                  <p className="text-xs text-muted-foreground">Official sources, API data</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/5 border border-warning/20">
                <HelpCircle className="w-5 h-5 text-warning" />
                <div>
                  <p className="text-sm font-medium text-foreground">Estimated</p>
                  <p className="text-xs text-muted-foreground">Derived from proxies</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <Cpu className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Modeled</p>
                  <p className="text-xs text-muted-foreground">Algorithm-generated</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MODULE A: Financial Module */}
        <ModuleSection 
          icon={DollarSign} 
          title="A. Financial Module" 
          subtitle="Economic valuation methodology"
        >
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-5">
              <MethodItem
                title="Income Multiplier Logic"
                description="Brand value is estimated using revenue-based multipliers calibrated to sports industry benchmarks. The multiplier adjusts for market position, growth trajectory, and competitive dynamics. Note: This formula requires federation-specific revenue data to fully activate. Current implementation uses benchmark-based anchors."
                formula="Brand Value = (Revenue Base × Industry Multiplier) × Growth Adjustment"
                formulaNote="Target-state formula — requires federation-specific revenue inputs for full calibration"
              />
              <div className="border-t border-border/50 my-4" />
              <MethodItem
                title="Relief-from-Royalty Reference"
                description="Alternative valuation cross-check using hypothetical royalty rates the entity would pay to license its own brand. Rates derived from comparable licensing transactions in professional sports. This module activates when projected revenue, royalty rate, and discount rate inputs are provided."
                formula="Value = Projected Revenue × Royalty Rate × Present Value Factor"
                formulaNote="Planned module — requires federation revenue projections, comparable royalty rates, and discount rate inputs"
              />
            </CardContent>
          </Card>
        </ModuleSection>

        {/* MODULE B: Digital Signal Module */}
        <ModuleSection 
          icon={Wifi} 
          title="B. Digital Signal Module" 
          subtitle="Real-time public data integration"
        >
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-5">
              <MethodItem
                title="Social Engagement Weighting"
                description="Platform-specific engagement rates normalized across Instagram, X/Twitter, TikTok, and YouTube. Weights account for platform demographics and sports audience concentration."
                formula="Engagement Score = Σ(Platform Weight × Engagement Rate × Reach Factor)"
              />
              <div className="border-t border-border/50 my-4" />
              <MethodItem
                title="Search Volume Normalization"
                description="Google Trends data normalized against category baselines and seasonal patterns. Accounts for tournament cycles and news event spikes to isolate organic interest."
                formula="Normalized Index = (Raw Volume / Category Baseline) × Seasonality Adj."
              />
              <div className="border-t border-border/50 my-4" />
              <MethodItem
                title="Sentiment Modeling (Planned Integration)"
                description="Planned module for sentiment analysis of media coverage and social mentions. When connected to a media monitoring provider, composite scores will be weighted by source authority and reach, with controversy decay factors. Current sentiment scores shown on the platform are modeled baselines."
                formula="Sentiment = (Positive% × 1.0) + (Neutral% × 0.5) - (Negative% × 1.5)"
                formulaNote="Scoring formula — activates with media monitoring integration. Current values are modeled baselines."
              />
            </CardContent>
          </Card>
        </ModuleSection>

        {/* MODULE C: Volatility Adjustment */}
        <ModuleSection 
          icon={Activity} 
          title="C. Volatility Adjustment" 
          subtitle="Risk and uncertainty factors"
        >
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-5">
              <MethodItem
                title="Injury Risk Factor"
                description="For player-dependent valuations, injury history and position-specific risk profiles adjust expected brand continuity. Applied as a probability-weighted discount."
                formula="Adjusted Value = Base Value × (1 - Injury Probability × Impact Severity)"
              />
              <div className="border-t border-border/50 my-4" />
              <MethodItem
                title="Tournament Impact Modeling"
                description="Scenario-based modeling of brand value sensitivity to tournament outcomes. Uses historical data from World Cups, continental championships, and league competitions."
                formula="Impact = Σ(Outcome Probability × Brand Value Change × Media Amplification)"
              />
            </CardContent>
          </Card>
        </ModuleSection>

        {/* MODULE D: Composite Index Structure */}
        <ModuleSection 
          icon={PieChart} 
          title="D. Composite Index Structure" 
          subtitle="Weight distribution across dimensions"
        >
          <Card className="bg-card/50 border-border/50">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground mb-6">
                The overall brand score aggregates six weighted dimensions into a 0-100 composite index. 
                Weights are calibrated based on empirical correlation with brand value outcomes.
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={weightData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {weightData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                
                {/* Legend */}
                <div className="space-y-3">
                  {weightData.map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-sm text-foreground">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-foreground">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Formula:</strong> Composite Score = Σ(Dimension Score × Dimension Weight) / 100
                </p>
              </div>
            </CardContent>
          </Card>
        </ModuleSection>

        {/* Institutional Disclaimer */}
        <Card className="bg-destructive/5 border-destructive/20 mb-10">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Institutional Disclaimer</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">This is a strategic brand intelligence tool, not an official financial valuation.</strong>
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                  The valuations, scores, and projections presented on this platform are based on public data signals 
                  and industry-recognized methodologies. They are intended for strategic planning, competitive benchmarking, 
                  and internal decision support purposes only. They do not constitute financial advice, certified appraisals, 
                  audited valuations, or investment recommendations. All figures should be treated as directional indicators 
                  subject to market conditions, data availability, and methodological assumptions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card className="bg-primary/5 border-primary/20 mb-10">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">
                  For questions about our methodology or to request a detailed technical whitepaper, 
                  contact our research team. Custom calibration available for institutional clients.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Ready to access strategic brand intelligence?</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/athletes">
              <Button variant="outline">
                View Individual Analytics
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/national-teams">
              <Button>
                Open Federation Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <PlatformFooter />
    </div>
  );
}
